import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchCustomerMovements } from './api';
import * as XLSX from 'xlsx';
import DocDetailsModal from './DocDetailsModal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { he } from 'date-fns/locale';


const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  width: 100%;
  height: calc(100vh - 120px);
  overflow: hidden;
  background-color: #f3f6fa;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background-color: white;
  color: #333;
  direction: rtl;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4a90e2;
    box-shadow: 0 0 0 2px rgba(26, 115, 232, 0.2);
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 6px 10px;
  }
`;

const Header = styled.h1`
  color: #1c4e8c;
  font-weight: bold;
  margin-bottom: 20px;
  font-size: 28px;

  @media (max-width: 768px) {
    font-size: 22px;
    text-align: center;
  }
`;

const FilterRow = styled.div`
  display: flex;
  flex-direction: row-reverse;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: flex-start;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;


const DateInput = styled.input`
  padding: 8px 12px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 6px 10px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 18px;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const Th = styled.th`
  background: #1c4e8c;
  color: white;
  padding: 12px;
  text-align: right;
  font-weight: bold;

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Td = styled.td`
  border: 1px solid #ddd;
  padding: 12px;
  text-align: right;
  cursor: pointer;

  &:hover {
    background-color: #f0f0f0;
  }

  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Button = styled.button`
  background: linear-gradient(135deg, #1c4e8c, #4a90e2);
  color: white;
  border: none;
  padding: 12px 20px;
  font-size: 16px;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  transition: 0.3s;
  font-weight: bold;

  &:hover {
    background: linear-gradient(135deg, #164b7a, #367bc3);
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    font-size: 14px;
  }
`;

const FilterColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const Label = styled.label`
  font-size: 14px;
  color: #1c4e8c;
  margin-bottom: 4px;

  @media (max-width: 768px) {
    font-size: 13px;
  }
`;

const LoaderOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(243, 246, 250, 0.6);
  backdrop-filter: blur(1.5px);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
`;

const Spinner = styled.div`
  border: 6px solid #f3f3f3;
  border-top: 6px solid #1c4e8c;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TableWrapperWithLoader = styled.div`
  position: relative;
  width: 95%;
  max-width: 1400px;
  max-height: 70vh;
  overflow-y: auto;
  background: white;
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.15);
  border-radius: 10px;
  padding: 20px;
  opacity: ${props => (props.loading ? 0.6 : 1)};
  transition: opacity 0.3s ease-in-out;

  @media (max-width: 768px) {
    padding: 10px;
  }
`;

const StyledDateInput = styled.input`
  padding: 8px 12px 8px 36px;
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  background: url('data:image/svg+xml;utf8,<svg fill="gray" height="20" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/></svg>') no-repeat 10px center;
  background-size: 20px 20px;
  direction: rtl;

  &::placeholder {
    color: #aaa;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    padding: 6px 10px 6px 36px;
  }
`;






const parseDotNetDate = (str) => {
  const match = str?.match(/\d+/);
  if (!match) return '';
  const date = new Date(parseInt(match[0]));
    return date.toLocaleString('he-IL', {
    dateStyle: 'short',
    timeStyle: 'short',
    hour12: false
    });
};


const getDocTypeName = (type) => {
  if (type === 6) return "תעודת כניסה";
  if (type === 7) return "תעודת יציאה";
  if (type === 8) return "כניסה לאוניית יצוא";
  return type;
};

const CustomerMovements = () => {
  const [movements, setMovements] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedDocCode, setSelectedDocCode] = useState(null);
  const [docTypeFilter, setDocTypeFilter] = useState('7');
  const [isLoading, setIsLoading] = useState(false);

  const formatDate = (d) => d.toISOString().split('T')[0];

  const loadMovements = async (custCode, from, to) => {
    const data = await fetchCustomerMovements(custCode, from, to);
    setMovements(data || []);
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('userData'));
    const custCode = userData?.Cust_Code;

    const today = new Date();
    const defaultDate = formatDate(today);

    setFromDate(defaultDate);
    setToDate(defaultDate);
    loadMovements(custCode, defaultDate, defaultDate);
  }, []);


const handleFilter = async () => {
  const userData = JSON.parse(localStorage.getItem('userData'));
  const custCode = userData?.Cust_Code;
  if (custCode && fromDate && toDate) {
    setIsLoading(true);
    await loadMovements(custCode, fromDate, toDate);
    setIsLoading(false);
  }
};

const exportToExcel = () => {
  const userData = JSON.parse(localStorage.getItem('userData'));
  const filtered = movements.filter(
    m => m.Cust_Code === userData?.Cust_Code &&
         (!docTypeFilter || String(m.Doc_Type) === docTypeFilter)
  );

  if (!filtered.length) return;

  // Format and prepare export-friendly data
  const exportData = filtered.map(m => ({
    'מספר מסמך': m.Doc_Code,
    'לקוח': m.Cust_Name,
    'ספק': m.Sup_Name,
    'שם אוניה': m.Store_Name,
    'תאריך': parseDotNetDate(m.Date),
    'נהג': m.DriverName,
    'מספר רכב': m.TruckNum,
    'יעד': m.Additional_1,
    'מק"ט': m.Makat,
    'תיאור': m.Des,
    'כמות': m.Qty,
    'משקל': m.Weight
  }));

  const ws = XLSX.utils.json_to_sheet(exportData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Documents');
  XLSX.writeFile(wb, 'customer_movements.xlsx');
};

const printTable = () => {
  const printContent = document.getElementById('customerTable').outerHTML;
  const newWindow = window.open('', '_blank');
  newWindow.document.write(`
    <html>
      <head>
        <title>הדפסת תנועות לקוח</title>
        <style>
          body {
            
            font-family: Arial;
            padding: 20px;
          }
          h2 {
            text-align: center;
            margin-bottom: 20px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            font-size: 12px;
            table-layout: auto;
          }
          th, td {
            border: 1px solid black;
            padding: 8px;
            text-align: right;
            vertical-align: top;
            word-wrap: break-word;
            white-space: normal;
          }
          th {
            background-color: #1c4e8c;
            color: white;
          }
        </style>
      </head>
      <body>
        <h2>תנועות לקוחות</h2>
        ${printContent}
        <script>
          window.onload = function() {
            window.print();
            setTimeout(() => window.close(), 500);
          };
        </script>
      </body>
    </html>
  `);
  newWindow.document.close();
};


 const groupedDocs = Object.values(
    movements.reduce((acc, curr) => {
        if (!acc[curr.Doc_Code]) {
        acc[curr.Doc_Code] = { ...curr, Items: [], TotalQty: 0 };
        }
        acc[curr.Doc_Code].Items.push(curr);
        acc[curr.Doc_Code].TotalQty += curr.Qty || 0;
        return acc;
    }, {})
    ).sort((a, b) => {
    const timeA = parseInt(a.Date?.match(/\d+/)?.[0] || 0);
    const timeB = parseInt(b.Date?.match(/\d+/)?.[0] || 0);
    return timeB - timeA;
    });


  return (
    <Container>
      <Header>תנועות לקוחות</Header>
        <FilterRow>

       <FilterColumn>
        <Label>מתאריך</Label>
        <DatePicker
            selected={fromDate ? new Date(fromDate) : null}
            onChange={(date) => setFromDate(date.toISOString().split('T')[0])}
            dateFormat="dd/MM/yyyy"
            locale={he}
            wrapperClassName="customDatePicker"
            customInput={<StyledDateInput />}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
        />
        </FilterColumn>

        <FilterColumn>
        <Label>עד תאריך</Label>
        <DatePicker
            selected={toDate ? new Date(toDate) : null}
            onChange={(date) => setToDate(date.toISOString().split('T')[0])}
            dateFormat="dd/MM/yyyy"
            locale={he}
            wrapperClassName="customDatePicker"
            customInput={<StyledDateInput />}
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
        />
        </FilterColumn>



        <FilterColumn>
            <Label>סוג מסמך</Label>
            <Select value={docTypeFilter} onChange={(e) => setDocTypeFilter(e.target.value)}>
            <option value="">הצג הכל</option>
            <option value="7">תעודת יציאה</option>
            <option value="8">כניסה לאוניית יצוא</option>
            </Select>
        </FilterColumn>

        <Button onClick={handleFilter}>חפש</Button>
        </FilterRow>


      <ButtonRow>
        <Button onClick={exportToExcel}>📥 יצוא לאקסל</Button>
        <Button onClick={printTable}>🖨️ הדפס</Button>
      </ButtonRow>

        <TableWrapperWithLoader loading={isLoading}>
            {isLoading && (
                <LoaderOverlay>
                <Spinner />
                </LoaderOverlay>
            )}

            <Table id="customerTable">
            <thead>
            <tr>
                <Th>סה"כ כמות</Th>
                <Th>משקל</Th>                
                <Th>יעד</Th>                
                <Th>מספר רכב</Th>
                <Th>נהג</Th>
                <Th>תאריך</Th>
                <Th>שם אוניה</Th>
                <Th>שם ספק</Th>
                <Th>לקוח</Th>
                <Th>סוג מסמך</Th>
                <Th>קוד מסמך</Th>
            </tr>
            </thead>
            <tbody>
            {groupedDocs
                // .filter(doc => !docTypeFilter || String(doc.Doc_Type) === docTypeFilter)
                  .filter(doc => {
                        const userData = JSON.parse(localStorage.getItem('userData'));
                        return (
                        (!docTypeFilter || String(doc.Doc_Type) === docTypeFilter) &&
                        doc.Cust_Code === userData?.Cust_Code
                        );
                    })
                .map((doc, idx) => (
                <tr key={idx} onClick={() => setSelectedDocCode(doc.Doc_Code)}>
                    <Td>{doc.TotalQty}</Td>
                    <Td>{doc.Weight}</Td>
                    <Td>{doc.Additional_1}</Td>                    
                    <Td>{doc.TruckNum}</Td>
                    <Td>{doc.DriverName}</Td>
                    <Td>{parseDotNetDate(doc.Date)}</Td>
                    <Td>{doc.Store_Name}</Td>
                    <Td>{doc.Sup_Name}</Td>                    
                    <Td>{doc.Cust_Name}</Td>
                    <Td>{getDocTypeName(doc.Doc_Type)}</Td>
                    <Td>{doc.Doc_Code}</Td>
                </tr>
                ))}
            </tbody>
            </Table>

        </TableWrapperWithLoader>


      {selectedDocCode && (
        <DocDetailsModal
          docCode={selectedDocCode}
          items={movements.filter(m => m.Doc_Code === selectedDocCode)}
          onClose={() => setSelectedDocCode(null)}
        />
      )}
    </Container>
  );
};

export default CustomerMovements;